import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import axios from 'axios'
import { Alert, Button, Col, Row } from 'reactstrap'

import CustomInput from '../../common/InputGroup'
import CustomTextarea from '../../common/CustomTextarea'
import CustomSelect from '../../common/CustomReactSelectComponent'
import CustomDatePicker from '../../common/ReactDatePicker/ReactDatePicker'
import AsyncSelect, { useAsyncSelect } from '../../common/AsyncSelect'
import { AddAttachment } from '../../common/FileAttachment/AddAttachment'
import { useFileAttachments } from '../../hooks/useFileAttachments'
import { closeModal } from '../../common/GlobalModal/redux/GlobalModalActions'
import { notificationDanger, notificationSuccess } from '../../utils/customToastify'
import {
  MAX_SHARED_FILES,
  MAX_SHARED_FILE_SIZE,
  REPORT_TYPES,
  REPORT_TYPE_OPTIONS,
  SHARED_FILE_ACCEPT,
} from './constants'
import { reportAgeDays, reportAgeLabel } from './columns'
import {
  createSharedResource,
  downloadSharedResource,
  getSharedResourcesForContact,
  readApiError,
  updateSharedResource,
} from './services'

const toDateOnly = (date) => (date ? new Date(date).toISOString().slice(0, 10) : '')

// Build the controlled contact options for edit mode. contactNames skips ids whose
// contact no longer resolves, so fall back to the raw id when the name is missing.
const contactsFromResource = (resource) =>
  (resource?.contactIds || []).map((id, index) => ({
    value: id,
    label: resource.contactNames?.[index] || `Contact #${id}`,
  }))

/**
 * Upload / edit form for a shared document. Registered as the global modal
 * 'shared_resource_modal' so both the library page and the contact-page panel open
 * it — the panel passes contactId/contactName to pre-link the contact.
 */
const SharedResourceForm = ({ resource = null, contactId = null, contactName = '', onSaved }) => {
  const dispatch = useDispatch()
  const { fetchOptions } = useAsyncSelect()
  const isEdit = Boolean(resource?.id)

  const [form, setForm] = useState({
    title: resource?.title || '',
    description: resource?.description || '',
    reportTypes: resource?.reportTypes || [],
    reportDate: resource?.reportDate ? new Date(resource.reportDate) : null,
    divisions: resource?.divisions || [],
    roles: resource?.roles || [],
    users: resource?.users || [],
  })
  const [selectedContacts, setSelectedContacts] = useState(() => {
    if (contactId) return [{ value: contactId, label: contactName || `Contact #${contactId}` }]
    return contactsFromResource(resource)
  })
  const [saving, setSaving] = useState(false)
  const [pickerOptions, setPickerOptions] = useState({ divisions: [], roles: [], users: [] })
  const [existingReports, setExistingReports] = useState([])

  const { attachments, onUploadDocuments, List } = useFileAttachments({ maxFiles: MAX_SHARED_FILES })

  const contactIds = useMemo(() => selectedContacts.map((contact) => contact.value), [selectedContacts])

  useEffect(() => {
    const loadPickerOptions = async () => {
      try {
        // Module's own endpoint: active users/roles only, labeled with their
        // division (several people have duplicate accounts under the same name).
        const response = await axios.get('/shared-resources/share-targets')
        setPickerOptions(response.data)
      } catch (error) {
        notificationDanger('Failed to load divisions/roles/users')
      }
    }
    loadPickerOptions()
  }, [])

  // Narrow the users picker to the selected divisions; the ACL itself is enforced
  // server-side, this is purely a UX convenience.
  const userOptions = useMemo(() => {
    const allUsers = pickerOptions.users || []
    if (!form.divisions.length) return allUsers
    return allUsers.filter((user) => form.divisions.includes(user.divisionId))
  }, [pickerOptions.users, form.divisions])

  // The "check here first" hint: when contacts are linked, surface the reports that
  // already exist for them so nobody pays for a duplicate pull.
  useEffect(() => {
    if (!contactIds.length) {
      setExistingReports([])
      return
    }
    Promise.all(contactIds.map((id) => getSharedResourcesForContact(id).catch(() => null)))
      .then((responses) => {
        const merged = new Map()
        responses.forEach((response) => {
          ;(response?.data?.data || []).forEach((report) => merged.set(report.id, report))
        })
        setExistingReports([...merged.values()])
      })
      .catch(() => setExistingReports([]))
  }, [contactIds.join(',')])

  const duplicates = useMemo(
    () =>
      existingReports.filter(
        (report) =>
          form.reportTypes.length &&
          (report.reportTypes || []).some((type) => form.reportTypes.includes(type)) &&
          report.id !== resource?.id
      ),
    [existingReports, form.reportTypes, resource]
  )

  const onFieldChange = (event) => {
    const { name, value } = event.target
    setForm((prev) => ({ ...prev, [name]: value }))
  }

  // Every select in this form is isMulti; react-select 3.x hands the handler null
  // (not []) when the last value is cleared, so coerce before mapping.
  const onSelectChange = (values, event) => {
    const items = Array.isArray(values) ? values : values ? [values] : []
    setForm((prev) => ({ ...prev, [event.name]: items.map((item) => item.id || item.value) }))
  }

  const loadContactOptions = (inputValue, options) =>
    fetchOptions('/contacts/contacts-for-async-select', { q: inputValue, ...options })

  const onContactsChange = (values) => {
    setSelectedContacts(Array.isArray(values) ? values : values ? [values] : [])
  }

  const submit = async () => {
    if (!form.title.trim()) return notificationDanger('Title is required')
    if (!form.reportTypes.length) return notificationDanger('Select at least one report type')
    if (!form.reportDate) return notificationDanger('Report date is required')
    if (!isEdit && !attachments.length) return notificationDanger('Add at least one file')

    try {
      setSaving(true)

      if (isEdit) {
        await updateSharedResource(resource.id, {
          title: form.title.trim(),
          description: form.description,
          reportTypes: form.reportTypes,
          reportDate: toDateOnly(form.reportDate),
          contactIds,
          divisions: form.divisions,
          roles: form.roles,
          users: form.users,
        })
        notificationSuccess('Document updated')
      } else {
        const formData = new FormData()
        formData.append('title', form.title.trim())
        formData.append('description', form.description || '')
        formData.append('reportTypes', JSON.stringify(form.reportTypes))
        formData.append('reportDate', toDateOnly(form.reportDate))
        formData.append('contactIds', JSON.stringify(contactIds))
        formData.append('divisions', JSON.stringify(form.divisions))
        formData.append('roles', JSON.stringify(form.roles))
        formData.append('users', JSON.stringify(form.users))
        attachments.forEach((file) => formData.append('files', file))
        await createSharedResource(formData)
        notificationSuccess('Document uploaded')
      }

      if (onSaved) onSaved()
      dispatch(closeModal('shared_resource_modal'))
    } catch (error) {
      notificationDanger(await readApiError(error, 'Failed to save the document'))
    } finally {
      setSaving(false)
    }
  }

  const downloadExisting = async (report) => {
    try {
      await downloadSharedResource(report.id, report.file?.name)
    } catch (error) {
      notificationDanger(await readApiError(error, 'Download failed'))
    }
  }

  return (
    <div className='p-3'>
      <Row>
        <Col md='6'>
          <CustomInput
            label='Title *'
            type='text'
            value={form.title}
            onChange={onFieldChange}
            name='title'
            placeholder='e.g. Cortera report - ACME LLC'
          />
        </Col>
        <Col md='3'>
          <CustomSelect
            label='Report Types *'
            defaultValue={form.reportTypes}
            onChange={onSelectChange}
            options={REPORT_TYPE_OPTIONS}
            name='reportTypes'
            placeholder='Select...'
            isClearable
            isMulti
          />
        </Col>
        <Col md='3'>
          <CustomDatePicker
            label='Report Date *'
            selected={form.reportDate}
            onChange={(value) => setForm((prev) => ({ ...prev, reportDate: value }))}
            dateFormat='MM/dd/yyyy'
            placeholder='When was it pulled...'
            className='form-control form-control-sm'
          />
        </Col>
      </Row>

      <Row>
        <Col md='7'>
          <CustomTextarea
            label='Description'
            value={form.description}
            onChange={onFieldChange}
            name='description'
            rows='2'
          />
        </Col>
        <Col md='5'>
          {contactId ? (
            <CustomInput
              label='Contact'
              type='text'
              value={contactName || `Contact #${contactId}`}
              name='contactDisplay'
              disabled
              onChange={() => {}}
            />
          ) : (
            <AsyncSelect
              label='Contacts (optional)'
              isClearable
              isMulti
              value={selectedContacts}
              onChange={onContactsChange}
              loadOptions={loadContactOptions}
              name='contactIds'
            />
          )}
        </Col>
      </Row>

      {!isEdit && duplicates.length > 0 && (
        <Alert color='warning'>
          <strong>A report of the selected type already exists for the linked contact(s):</strong>
          {duplicates.slice(0, 3).map((report) => (
            <div key={report.id} className='d-flex align-items-center'>
              <span>
                {report.title} ({(report.reportTypes || []).map((type) => REPORT_TYPES[type] || type).join(', ')}) —{' '}
                {reportAgeLabel(reportAgeDays(report.reportDate))}, uploaded by {report.createdByObj?.name || 'unknown'}
              </span>
              <Button color='link' size='sm' className='p-0 ml-2' onClick={() => downloadExisting(report)}>
                Download it instead
              </Button>
            </div>
          ))}
        </Alert>
      )}

      <hr />
      <p className='mb-1'>
        <strong>Share with</strong>{' '}
        <small className='text-muted'>
          — visible to anyone matching ANY selection. Leave all empty to make the document visible to everyone.
        </small>
      </p>
      <Row>
        <Col md='4'>
          <CustomSelect
            label='Divisions'
            defaultValue={form.divisions}
            onChange={onSelectChange}
            options={pickerOptions.divisions}
            name='divisions'
            placeholder='Select divisions...'
            isClearable
            isMulti
          />
        </Col>
        <Col md='4'>
          <CustomSelect
            label='Roles'
            defaultValue={form.roles}
            onChange={onSelectChange}
            options={pickerOptions.roles}
            name='roles'
            placeholder='Select roles...'
            isClearable
            isMulti
          />
        </Col>
        <Col md='4'>
          <CustomSelect
            label='Users'
            defaultValue={form.users}
            onChange={onSelectChange}
            options={userOptions}
            name='users'
            placeholder='Select users...'
            isClearable
            isMulti
          />
        </Col>
      </Row>

      {!isEdit && (
        <>
          <hr />
          <AddAttachment
            multiple
            onUploadDocuments={onUploadDocuments}
            maxAttachmentFiles={MAX_SHARED_FILES}
            maxFileSize={MAX_SHARED_FILE_SIZE}
            accept={SHARED_FILE_ACCEPT}
          />
          {List}
        </>
      )}

      <div className='text-right mt-3'>
        <Button className='btn-gold' disabled={saving} onClick={submit}>
          {saving ? 'Saving...' : isEdit ? 'Save Changes' : 'Upload'}
        </Button>
      </div>
    </div>
  )
}

export default SharedResourceForm
